import axios from 'axios';

import { SourceCamera, SourceConfig } from '../types';

type RhombusCameraState = {
  uuid: string;
  name: string;
  serialNumber?: string;
  lanAddresses?: string[];
  externalIPAddress?: string;
  connectionStatus?: string;
  locationUuid?: string;
  firmwareVersion?: string;
};

async function rhombusProxyPost(
  url: string,
  body: Record<string, string>
) {
  const { data } = await axios.post(url, body);

  if (data.error) {
    throw new Error(data.errorMsg || 'Rhombus API returned an error');
  }

  return data;
}

async function createRawHttpStream(
  apiBaseUrl: string,
  apiKey: string,
  cameraId: string,
  streamName: string,
  quality: string
): Promise<string> {
  const data = await rhombusProxyPost(
    `${apiBaseUrl}/integrations/rhombus/local_stream`,
    { api_key: apiKey, camera_id: cameraId, name: streamName }
  );

  const url =
    quality === 'low' ? data.lanVideoLowResUrl : data.lanVideoUrl;

  if (!url) {
    throw new Error('No local stream URL returned from Rhombus');
  }

  return url;
}

async function createSharedLiveVideoStream(
  apiBaseUrl: string,
  apiKey: string,
  cameraId: string,
  name: string
): Promise<string> {
  const data = await rhombusProxyPost(
    `${apiBaseUrl}/integrations/rhombus/cloud_stream`,
    { api_key: apiKey, camera_id: cameraId, name }
  );

  const url = data.sharedLiveM3U8StreamUrl;
  if (!url) {
    throw new Error('No cloud stream URL returned from Rhombus');
  }

  return url;
}

export const rhombusConfig: SourceConfig = {
  label: 'Rhombus',
  description: 'Import cameras from Rhombus',
  docsUrl: 'https://docs.lumeo.com/docs/rhombus-camera-import',
  credentialFields: [
    {
      name: 'api_key',
      label: 'API Key',
      type: 'password',
      required: true,
      helpText:
        'Rhombus API key with Device (View and Manage) and Video (Share & Download) permissions.',
      helpUrl: 'https://console.rhombussystems.com/settings/integrations/api-tokens',
    },
    {
      name: 'stream_mode',
      label: 'Stream Type',
      type: 'select',
      required: true,
      defaultValue: 'cloud',
      options: [
        { label: 'Cloud stream (HLS)', value: 'cloud' },
        { label: 'Local stream (raw HTTP)', value: 'local' },
      ],
      helpText:
        'Cloud streams are accessible from anywhere. Local streams require network access to the camera. Before importing local streams, ensure the camera is set to static IP.',
    },
  ],
  qualityOptions: [
    { label: 'Standard', value: 'standard' },
    { label: 'Low', value: 'low' },
  ],
  defaultQuality: 'standard',

  async fetchCameras(
    credentials: Record<string, string>,
    apiBaseUrl: string
  ): Promise<SourceCamera[]> {
    const data = await rhombusProxyPost(
      `${apiBaseUrl}/integrations/rhombus/cameras`,
      { api_key: credentials.api_key }
    );

    const cameras: RhombusCameraState[] = data.cameraStates ?? [];

    return cameras
      .map((cam) => ({
        id: cam.uuid,
        name: cam.name || cam.uuid,
        location: cam.locationUuid ?? '',
        serial: cam.serialNumber,
        mac: cam.lanAddresses?.[0],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  async buildStreamUri(
    camera: SourceCamera,
    credentials: Record<string, string>,
    quality: string,
    apiBaseUrl: string
  ): Promise<string> {
    const streamMode = credentials.stream_mode || 'cloud';
    const streamName = `Lumeo - ${camera.name}`;

    if (streamMode === 'local') {
      return createRawHttpStream(
        apiBaseUrl,
        credentials.api_key,
        camera.id,
        streamName,
        quality === 'low' ? 'low' : 'standard'
      );
    }

    return createSharedLiveVideoStream(
      apiBaseUrl,
      credentials.api_key,
      camera.id,
      streamName
    );
  },

  buildStreamName(camera: SourceCamera, quality: string): string {
    const suffix = quality === 'low' ? ' (low)' : '';
    return `${camera.name}${suffix}`;
  },
};
